import { useCallback, useRef } from 'react';
import {
    clampView,
    isFitted,
    panOverflowX,
    pinchAnchor,
    pinchSpread,
    stepView,
} from './zoomFrame';

/**
 * Which fingers are on the picture, and what they are asking for (SPEC-017 D-4).
 *
 * Split out of `usePinchZoom`, which owns where the picture IS; this owns how it
 * is being moved. The seam is the one the 350-line contract asked for and it is
 * a real one — everything here is bookkeeping that ends the moment the last
 * pointer lifts, and everything there outlives every gesture.
 *
 * Pointer Events only. No `touchstart`, no WebKit `gesturechange`: a second
 * event family for the same gesture is a second way to do an established thing,
 * and on iOS the two disagree in ways nothing in the suite would catch.
 *
 * The rule that makes the carousel work is the early return in `onPointerMove`.
 * While the picture fits, one finger on it belongs to the RAIL — the browser's
 * own scroll-snap, with the momentum and rubber-banding that come with it. We
 * take the gesture only when there is something to move: a second finger, or a
 * picture already zoomed (SPEC-017 D-2).
 */

/** How much of a screen a drag past the picture's edge commits to the next one. */
const PAGE_COMMIT = 0.25;

/**
 * How long to wait for a glide to land before giving the rail its snapping back
 * regardless. `scrollend` is the real signal; this is the floor under engines
 * that do not fire it yet, not the plan.
 */
const GLIDE_CEILING_MS = 700;

export function usePictureGesture({
    frameRef,
    railRef,
    pointersRef,
    viewRef,
    boxes,
    apply,
    frameOrigin,
    toggleZoom,
    onIdle,
}) {
    const gestureRef = useRef(null);

    const livePoints = useCallback((origin) => (
        Array.from(pointersRef.current.values(), (point) => ({
            x: point.x - origin.x,
            y: point.y - origin.y,
        }))
    ), [pointersRef]);

    /**
     * Re-read the gesture from where it is now.
     *
     * Called whenever the number of fingers changes, so that adding or lifting
     * one re-bases the arithmetic instead of teleporting the picture by the
     * difference between a one-finger anchor and a two-finger one.
     */
    const rebase = useCallback((event) => {
        const rail = railRef.current;
        const origin = frameOrigin();
        const points = livePoints(origin);
        const previous = gestureRef.current;
        const width = Math.max(rail?.clientWidth ?? 1, 1);

        gestureRef.current = {
            origin,
            view: viewRef.current,
            anchor: pinchAnchor(points),
            spread: points.length >= 2 ? pinchSpread(points) : 0,
            // Where the rail sits now, so a drag past the picture's edge is
            // measured from a whole page rather than from mid-flick.
            pageLeft: rail ? Math.round(rail.scrollLeft / width) * width : 0,
            // Carried across a re-base: a gesture that has already moved the
            // rail is still the same gesture when a finger is added or lifted.
            railMoved: previous?.railMoved ?? false,
        };
    }, [frameOrigin, livePoints, railRef, viewRef]);

    /** The share of a drag the picture refused, spent on the rail instead. */
    const dragRail = useCallback((overflow) => {
        const rail = railRef.current;
        const gesture = gestureRef.current;

        if (!rail || (!overflow && !gesture.railMoved)) {
            return;
        }

        // Snapping off for the duration: `scroll-snap-type: x mandatory` re-snaps
        // a programmatic `scrollLeft` on the spot, which would fight the finger
        // on every frame of the hand-off (SPEC-017 D-2).
        rail.dataset.dragging = 'true';
        gesture.railMoved = true;
        rail.scrollLeft = gesture.pageLeft - overflow;
    }, [railRef]);

    const releaseRail = useCallback(() => {
        const rail = railRef.current;
        const gesture = gestureRef.current;

        if (!rail || !gesture?.railMoved) {
            return;
        }

        const width = Math.max(rail.clientWidth, 1);
        const moved = (rail.scrollLeft - gesture.pageLeft) / width;
        const committed = Math.abs(moved) > PAGE_COMMIT ? Math.sign(moved) : 0;

        // The rail GLIDES to the page the drag committed to rather than arriving
        // there. It used to be an assignment, which is a cut at the one moment
        // the reader is watching the thing move — their finger has just left a
        // picture that was travelling under it.
        //
        // Snapping stays off until the glide lands. Restoring it first makes the
        // engine re-snap to ITS nearest page, which is a jump in the middle of an
        // animation to ours.
        delete rail.dataset.dragging;
        rail.dataset.gliding = 'true';
        // This read is load-bearing and looks like nothing. `scrollTo` resolves
        // `scroll-behavior` from the COMPUTED style at the moment it is called,
        // and the attribute set on the line above has not been recalculated yet
        // — so without forcing it here the glide is issued against the old
        // `auto` and lands as the cut this whole change exists to remove.
        window.getComputedStyle(rail).scrollBehavior;
        // No `behavior` argument on purpose: the state's own `scroll-behavior` is
        // what decides, so `prefers-reduced-motion` is answered in the sheet
        // rather than by a second media-query read in here.
        rail.scrollTo({ left: gesture.pageLeft + committed * width });

        const landed = () => {
            window.clearTimeout(ceiling);
            rail.removeEventListener('scrollend', landed);
            delete rail.dataset.gliding;
        };
        const ceiling = window.setTimeout(landed, GLIDE_CEILING_MS);

        rail.addEventListener('scrollend', landed);
    }, [railRef]);

    const onPointerDown = useCallback((event) => {
        pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
        rebase(event);

        if (pointersRef.current.size < 2 && isFitted(viewRef.current)) {
            return;
        }

        // Every live pointer, not just this one: a pinch that starts from a
        // fitted picture has a first finger that was the rail's a moment ago.
        pointersRef.current.forEach((point, id) => {
            try {
                frameRef.current.setPointerCapture(id);
            } catch (uncapturable) {
                // Capture is an optimisation — it keeps the moves coming when a
                // finger wanders off the picture — and a pointer the browser no
                // longer considers active simply cannot wander. Swallowed rather
                // than dropped from the map: dropping it would end a gesture the
                // reader is still making.
            }
        });
        // Re-applied unchanged, for `data-seized`: `touch-action` has to be ours
        // before the next pointer event rather than after the next render.
        apply(viewRef.current);
    }, [apply, frameRef, pointersRef, rebase, viewRef]);

    const onPointerMove = useCallback((event) => {
        const gesture = gestureRef.current;

        if (!gesture || !pointersRef.current.has(event.pointerId)) {
            return;
        }

        pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });

        // The rail's swipe. Nothing below would move the picture anyway — the
        // clamp gives a fitted one nowhere to go — but returning before we touch
        // the rail is what keeps the browser's own scroll uninterrupted.
        if (pointersRef.current.size < 2 && isFitted(viewRef.current)) {
            return;
        }

        const points = livePoints(gesture.origin);
        const spread = points.length >= 2 && gesture.spread > 0
            ? pinchSpread(points) / gesture.spread
            : 1;
        // Recomputed from the gesture's START every frame rather than stepped
        // from the last one: a fold of per-frame deltas accumulates its own
        // rounding, and the anchor is meant to be exact.
        const proposed = stepView(gesture.view, {
            scale: gesture.view.scale * spread,
            from: gesture.anchor,
            to: pinchAnchor(points),
        });
        const { frame, fitted: size } = boxes();

        apply(clampView(proposed, frame, size));

        if (points.length === 1) {
            dragRail(panOverflowX(proposed, frame, size));
        }
    }, [apply, boxes, dragRail, livePoints, pointersRef, viewRef]);

    const onPointerUp = useCallback((event) => {
        if (!pointersRef.current.delete(event.pointerId) || !gestureRef.current) {
            return;
        }

        if (pointersRef.current.size > 0) {
            rebase(event);

            return;
        }

        releaseRail();
        gestureRef.current = null;
        apply(viewRef.current);
        // Last, and only here: anything the viewer asked for mid-gesture — a
        // re-fit, because the hand-off had already changed the active slide —
        // has been waiting for the reader to let go.
        onIdle();
    }, [apply, onIdle, pointersRef, rebase, releaseRail, viewRef]);

    /**
     * The double tap, as the platform reports it.
     *
     * `dblclick` and not a pair of `pointerup`s we counted ourselves — see the
     * note at the top of this file. It is also, conveniently, the same event a
     * mouse produces on a double click, so the desktop gets the gesture for
     * nothing rather than through a second code path.
     */
    const onDoubleClick = useCallback((event) => {
        // A double tap that lands at the end of a drag is not a double tap. iOS
        // will not have produced one anyway; a trackpad can.
        if (gestureRef.current?.railMoved) {
            return;
        }

        event.preventDefault();
        toggleZoom({ x: event.clientX, y: event.clientY });
    }, [toggleZoom]);

    return {
        onPointerDown,
        onPointerMove,
        onPointerUp,
        onPointerCancel: onPointerUp,
        onDoubleClick,
    };
}
